import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { v4 as uuidv4 } from 'uuid';
import { lookupsJson } from '@ourbranch/lookups';
import { Formik, Form, useFormikContext } from 'formik';
import classNames from 'classnames';

import { FormField, FormSubmit } from 'core/components/form';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import {
  ENTITY_TYPE_OPTIONS,
  getDescriptionLabel,
  getVisibilityFlags
} from 'common/helpers/farm-interested-party-helpers';
import { getValidationSchema } from './add-farm-interested-party.validation';
import useStyles from '../../people/add-form.styles';

const FormContent = ({ disabled, classes, locationOptions, equipmentOptions, riskOptions, sppOptions, rpsOptions }) => {
  const { values } = useFormikContext();
  const { inPolicyRoute } = useRouteHelper();
  const {
    showLocationFields,
    showEquipmentFields,
    showLocationAndRiskFields,
    showLocationRiskAndEquipmentFields,
    showDescriptionOnly,
    showAllFields
  } = getVisibilityFlags(values.relationship);
  const context = inPolicyRoute ? 'policy' : 'offer';
  const emptyMsg = (itemType) =>
    `There are no ${itemType} on this ${context}. Please add one if you want to associate it to the interested party.`;

  return (
    <Form disabled={disabled} className={classes.container} id="addInterestedParty">
      <Grid
        key="container"
        container
        justifyContent="space-around"
        alignItems="flex-start"
        className={classes.formContainer}
        spacing={4}
      >
        <FormField
          mode="light"
          name="name"
          id="name"
          label="Name or Company"
          type="string"
          xs={12}
          permissions={{ isLicensedAction: false }}
        />
        <FormField
          mode="light"
          name="address"
          id="address"
          label="Address"
          type="address"
          xs={8}
          permissions={{ isLicensedAction: false }}
        />
        <FormField
          mode="light"
          name="address2"
          id="address2"
          label="Address 2"
          type="string"
          optional
          xs={4}
          permissions={{ isLicensedAction: false }}
        />
        <FormField
          mode="light"
          name="city"
          id="city"
          label="City"
          type="string"
          optional
          xs={4}
          permissions={{ isLicensedAction: false }}
        />
        <FormField
          mode="light"
          name="state"
          id="state"
          label="State"
          type="state"
          optional
          xs={4}
          permissions={{ isLicensedAction: false }}
        />
        <FormField
          mode="light"
          name="zip"
          id="zip"
          label="Zip code"
          type="string"
          optional
          xs={4}
          permissions={{ isLicensedAction: false }}
        />
        <FormField
          mode="light"
          name="relationship"
          id="relationship"
          label="Relationship"
          type="select"
          options={lookupsJson.farm.farmInterestedPartyTypes}
          xs={12}
          permissions={{ isLicensedAction: false }}
        />
        {values.relationship === 'partnerOrCoOwner' && (
          <FormField
            mode="light"
            name="entityType"
            id="entityType"
            label="Entity Type"
            type="select"
            options={ENTITY_TYPE_OPTIONS}
            xs={12}
            permissions={{ isLicensedAction: false }}
          />
        )}
        {showLocationFields && (
          <>
            <FormField
              mode="light"
              name="locations"
              id="locations"
              label="Locations"
              description="Select all that apply"
              type="chipArray"
              options={locationOptions}
              emptyMessage={emptyMsg('locations')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            {values.relationship === 'occupant' && (
              <FormField
                mode="light"
                name="isResident"
                id="isResident"
                label="Is this additional insured a resident of the household?"
                type="checkbox"
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
            )}
            <FormField
              mode="light"
              name="description"
              id="description"
              label={getDescriptionLabel(values.relationship)}
              type="textarea"
              minRows={3}
              maxRows={3}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          </>
        )}
        {showEquipmentFields && (
          <>
            <FormField
              mode="light"
              name="equipment"
              id="equipment"
              label="Associated equipment"
              description="Select all that apply"
              type="chipArray"
              options={equipmentOptions}
              emptyMessage={emptyMsg('equipment')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="description"
              id="description"
              label="Describe any leased equipment not listed on the policy (if applicable)"
              type="textarea"
              minRows={3}
              maxRows={3}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          </>
        )}
        {showLocationAndRiskFields && (
          <>
            <FormField
              mode="light"
              name="locations"
              id="locations"
              label="Associated locations/properties"
              description="Select all that apply"
              type="chipArray"
              options={locationOptions}
              emptyMessage={emptyMsg('locations')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="risks"
              id="risks"
              label="Associated risks"
              description="Select all that apply"
              type="chipArray"
              options={riskOptions}
              emptyMessage={emptyMsg('risks')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="description"
              id="description"
              label="Description"
              type="textarea"
              minRows={3}
              maxRows={3}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          </>
        )}
        {showLocationRiskAndEquipmentFields && (
          <>
            <FormField
              mode="light"
              name="locations"
              id="locations"
              label="Associated locations/properties"
              description="Select all that apply"
              type="chipArray"
              options={locationOptions}
              emptyMessage={emptyMsg('locations')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="risks"
              id="risks"
              label="Associated risks"
              description="Select all that apply"
              type="chipArray"
              options={riskOptions}
              emptyMessage={emptyMsg('risks')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="equipment"
              id="equipment"
              label="Associated equipment"
              description="Select all that apply"
              type="chipArray"
              options={equipmentOptions}
              emptyMessage={emptyMsg('equipment')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="description"
              id="description"
              label="Description"
              type="textarea"
              minRows={3}
              maxRows={3}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          </>
        )}
        {showDescriptionOnly && (
          <FormField
            mode="light"
            name="description"
            id="description"
            label="Description"
            type="textarea"
            minRows={3}
            maxRows={3}
            xs={12}
            permissions={{ isLicensedAction: false }}
          />
        )}
        {showAllFields && (
          <>
            <FormField
              mode="light"
              name="locations"
              id="locations"
              label="Associated locations/properties"
              description="Select all that apply"
              type="chipArray"
              options={locationOptions}
              emptyMessage={emptyMsg('locations')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="risks"
              id="risks"
              label="Associated risks"
              description="Select all that apply"
              type="chipArray"
              options={riskOptions}
              emptyMessage={emptyMsg('risks')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="equipment"
              id="equipment"
              label="Associated equipment"
              description="Select all that apply"
              type="chipArray"
              options={equipmentOptions}
              emptyMessage={emptyMsg('equipment')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="sppItems"
              id="sppItems"
              label="Associated inland marine assets"
              description="Select all that apply"
              type="chipArray"
              options={sppOptions}
              emptyMessage={emptyMsg('inland marine assets')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="rpsItems"
              id="rpsItems"
              label="Associated related private structures"
              description="Select all that apply"
              type="chipArray"
              options={rpsOptions}
              emptyMessage={emptyMsg('related private structures')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="description"
              id="description"
              label={getDescriptionLabel(values.relationship)}
              type="textarea"
              minRows={3}
              maxRows={3}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          </>
        )}
      </Grid>
      <Grid container justifyContent="flex-end" alignItems="center" spacing={4}>
        <FormSubmit
          mode="big"
          className={classNames(classes.button, classes.addButton)}
          variant="contained"
          color="secondary"
          xs={4}
        >
          Add Farm Interested Party
        </FormSubmit>
      </Grid>
    </Form>
  );
};

function AddFarmInterestedPartyForm({
  onAdd,
  disabled,
  locationOptions,
  equipmentOptions,
  riskOptions,
  sppOptions,
  rpsOptions,
  isPolicy
}) {
  const classes = useStyles();

  return (
    <Formik
      initialValues={{
        id: uuidv4(),
        name: undefined,
        address: undefined,
        relationship: undefined,
        address2: undefined,
        city: undefined,
        state: undefined,
        zip: undefined,
        locations: [],
        equipment: [],
        risks: [],
        sppItems: [],
        rpsItems: [],
        description: undefined,
        isResident: false,
        entityType: undefined
      }}
      validationSchema={getValidationSchema(isPolicy)}
      onSubmit={({
        id,
        name,
        relationship,
        address,
        address2,
        city,
        state,
        zip,
        locations,
        equipment,
        risks,
        sppItems,
        rpsItems,
        description,
        isResident,
        entityType
      }) =>
        onAdd({
          id,
          name,
          relationship,
          address: { address, address2, city, state, zip },
          locations,
          risks,
          equipment,
          sppItems,
          rpsItems,
          description,
          isResident,
          entityType
        })
      }
    >
      {() => (
        <FormContent
          disabled={disabled}
          classes={classes}
          locationOptions={locationOptions}
          equipmentOptions={equipmentOptions}
          riskOptions={riskOptions}
          sppOptions={sppOptions}
          rpsOptions={rpsOptions}
        />
      )}
    </Formik>
  );
}

FormContent.propTypes = {
  disabled: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  locationOptions: PropTypes.array,
  equipmentOptions: PropTypes.array,
  riskOptions: PropTypes.array,
  sppOptions: PropTypes.array,
  rpsOptions: PropTypes.array
};

FormContent.defaultProps = {
  disabled: false,
  locationOptions: [],
  equipmentOptions: [],
  riskOptions: [],
  sppOptions: [],
  rpsOptions: []
};

AddFarmInterestedPartyForm.propTypes = {
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  locationOptions: PropTypes.array,
  equipmentOptions: PropTypes.array,
  riskOptions: PropTypes.array,
  sppOptions: PropTypes.array,
  rpsOptions: PropTypes.array,
  isPolicy: PropTypes.bool
};
AddFarmInterestedPartyForm.defaultProps = {
  disabled: false,
  locationOptions: [],
  equipmentOptions: [],
  riskOptions: [],
  sppOptions: [],
  rpsOptions: [],
  isPolicy: false
};

export default AddFarmInterestedPartyForm;
